import './PrecheckGeneric.css';

import {
  GenericStatus,
  PrecheckState,
  RequestState,
  PrecheckPlaceholder,
  ManualPreCheckError
} from 'vrlcm-gui-types';
import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  getAlertGroups,
  getControlText,
  getControlType
} from './element-utils';

import FetchError from './FetchError';
import PrecheckAlerts from './PrecheckAlerts';
import PrecheckControl from './PrecheckControl';
import PrecheckResults from './PrecheckResults';
import { downloadReportLazy } from './util';
import { usePrecheckState } from './usePrecheckState';

interface Props {
  requestId: string | undefined;
  reportTitle?: string;
  precheckPlaceholder?: PrecheckPlaceholder;
  onRunValidations: () => void;
  onChangeStatus?: (status: GenericStatus) => void;
  alertConfig?: {
    info?: JSX.Element[];
    success?: JSX.Element[];
    failed?: JSX.Element[];
  };
  initialCollapsed?: boolean;
  manualPrecheckError?: ManualPreCheckError;
}

const PrecheckGeneric: FC<Props> = props => {
  const {
    requestId,
    reportTitle,
    precheckPlaceholder,
    onRunValidations,
    onChangeStatus,
    alertConfig,
    initialCollapsed,
    manualPrecheckError
  } = props;
  const [state, error] = usePrecheckState(requestId, manualPrecheckError);
  const { lastUpdateTimeMillis } = state;

  const [requestStatus, setRequestStatus] = useState(
    typeof requestId === 'string'
      ? GenericStatus.Pending
      : GenericStatus.NotStarted
  );
  const [consentGiven, setConsentGiven] = useState(false);
  const onConsentChange = useCallback((v: boolean) => {
    setConsentGiven(v);
  }, []);

  useEffect(() => {
    if (manualPrecheckError?.isError) setRequestStatus(GenericStatus.Failed);
  }, [manualPrecheckError]);

  const computeAndSetStatus = useCallback(
    (s: PrecheckState) => {
      if (typeof s.requestState === 'string') {
        let status = GenericStatus.Pending;
        switch (s.requestState) {
          case RequestState.CANCELLED:
            status = GenericStatus.Cancelled;
            break;
          case RequestState.FAILED:
            status = consentGiven
              ? GenericStatus.Failed
              : GenericStatus.ConsentRequired;
            break;
          case RequestState.NEVER_INITIATED:
            status = GenericStatus.NotStarted;
            break;
          case RequestState.COMPLETED:
            if (s.overallStatus) {
              status = consentGiven
                ? GenericStatus.Successful
                : GenericStatus.ConsentRequired;
            } else {
              status = consentGiven
                ? GenericStatus.Failed
                : GenericStatus.ConsentRequired;
            }
            break;
          case RequestState.INITIATED:
          case RequestState.INPROGRESS:
            status = GenericStatus.Running;
            break;
        }
        setRequestStatus(status);
      }
    },
    [consentGiven]
  );

  useEffect(() => {
    computeAndSetStatus(state);
  }, [computeAndSetStatus, state]);

  useEffect(() => {
    if (onChangeStatus) {
      onChangeStatus(requestStatus);
    }
  }, [requestStatus, onChangeStatus]);

  const onRunPrecheck = () => {
    setRequestStatus(GenericStatus.Pending);
    onRunValidations();
  };

  const onClickDownload = useCallback(() => {
    if (state.groups.length > 0) {
      downloadReportLazy(state, reportTitle);
    }
  }, [state, reportTitle]);

  const alertGroups = getAlertGroups(
    requestStatus,
    alertConfig,
    manualPrecheckError
  );
  const controlType = getControlType(requestStatus);
  const controlText = getControlText(requestStatus);
  const showDownloadReport =
    requestStatus === GenericStatus.Failed ||
    requestStatus === GenericStatus.ConsentRequired ||
    requestStatus === GenericStatus.Successful;

  return error ? (
    <FetchError />
  ) : (
    <div className="precheck-container">
      <PrecheckAlerts alertGroups={alertGroups} />
      <PrecheckControl
        type={controlType}
        text={controlText}
        showDownloadReport={showDownloadReport}
        lastUpdateTimeMillis={lastUpdateTimeMillis}
        onClickValidate={onRunPrecheck}
        onClickDownload={onClickDownload}
      />
      {requestStatus !== GenericStatus.Cancelled && (
        <PrecheckResults
          state={state}
          requestStatus={requestStatus}
          placeholder={precheckPlaceholder}
          onConsentChange={onConsentChange}
          initialCollapsed={initialCollapsed}
        />
      )}
    </div>
  );
};

export default PrecheckGeneric;
